import { useState, useEffect } from 'react';
import axios from 'axios';
import { Users, Truck, UserCheck, Megaphone, Layout, Award, Save, Mail, Instagram, MessageSquare } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const InputField = ({ label, name, value, onChange, type = 'number', placeholder = '' }) => (
  <div>
    <Label className="text-xs">{label}</Label>
    <Input
      type={type}
      value={value}
      onChange={(e) => onChange(name, type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value)}
      placeholder={placeholder}
      className="bg-secondary/50 h-9 text-sm"
    />
  </div>
);

const RatingSlider = ({ label, value, onChange, description }) => (
  <div className="space-y-1">
    <div className="flex justify-between">
      <Label className="text-xs">{label}</Label>
      <span className="text-xs font-bold text-primary">{value}/10</span>
    </div>
    <input
      type="range"
      min="1"
      max="10"
      value={value}
      onChange={(e) => onChange(parseInt(e.target.value))}
      className="w-full h-2 bg-secondary rounded-lg appearance-none cursor-pointer accent-primary"
    />
    {description && <p className="text-[10px] text-muted-foreground">{description}</p>}
  </div>
);

export default function EventAnalyticsData({ eventId, eventName }) {
  const [activeTab, setActiveTab] = useState('customer');
  const [saving, setSaving] = useState(false);
  
  // Customer Capture Data
  const [customerData, setCustomerData] = useState({
    emails_collected: 0, sms_collected: 0, instagram_follows: 0, qr_scans: 0,
    requested_items: '', common_objections: ''
  });
  
  // Logistics Data
  const [logisticsData, setLogisticsData] = useState({
    bins_totes_count: 0, load_in_minutes: 0, load_out_minutes: 0,
    setup_minutes: 0, teardown_minutes: 0, missing_items: '',
    damaged_items: '', theft_incidents: '', notes: ''
  });
  
  // Staffing Data
  const [staffingData, setStaffingData] = useState({
    staff_count: 0, total_staff_hours: 0, booth_energy_rating: 5, notes: ''
  });
  
  // Marketing Data
  const [marketingData, setMarketingData] = useState({
    pre_show_posts: 0, pre_show_reels_views: 0, link_clicks: 0, preorders_booked: 0,
    qr_code_scans: 0, giveaway_entries: 0, discount_code_uses: 0, mention_redemptions: 0,
    post_show_sales_spike: 0, new_followers_converted: 0, notes: ''
  });
  
  // Booth Data
  const [boothData, setBoothData] = useState({
    booth_visitors: 0, transactions: 0, stop_rate_estimate: 50,
    best_wall_product: '', first_stop_area: '', most_touched_product: '',
    ignored_areas: '', card_payment_percent: 50, cash_payment_percent: 50,
    failed_transactions: 0, wifi_issues: false, notes: ''
  });
  
  // Show Ranking
  const [rankingData, setRankingData] = useState({
    profit_score: 5, stress_score: 5, travel_difficulty_score: 5,
    brand_growth_score: 5, would_do_again: true, notes: ''
  });

  useEffect(() => {
    fetchAllData();
  }, [eventId]);

  const fetchAllData = async () => {
    const token = localStorage.getItem('vyrus_token');
    const headers = { Authorization: `Bearer ${token}` };
    
    try {
      const [customerRes, logisticsRes, staffingRes, marketingRes, boothRes, rankingRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/customer-capture/${eventId}`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${BACKEND_URL}/api/event-logistics/${eventId}`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${BACKEND_URL}/api/staffing-metrics/${eventId}`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${BACKEND_URL}/api/marketing-metrics/${eventId}`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${BACKEND_URL}/api/booth-metrics/${eventId}`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${BACKEND_URL}/api/show-ranking/${eventId}`, { headers }).catch(() => ({ data: {} })),
      ]);
      
      if (customerRes.data && Object.keys(customerRes.data).length) setCustomerData(prev => ({ ...prev, ...customerRes.data }));
      if (logisticsRes.data && Object.keys(logisticsRes.data).length) setLogisticsData(prev => ({ ...prev, ...logisticsRes.data }));
      if (staffingRes.data && Object.keys(staffingRes.data).length) setStaffingData(prev => ({ ...prev, ...staffingRes.data }));
      if (marketingRes.data && Object.keys(marketingRes.data).length) setMarketingData(prev => ({ ...prev, ...marketingRes.data }));
      if (boothRes.data && Object.keys(boothRes.data).length) setBoothData(prev => ({ ...prev, ...boothRes.data }));
      if (rankingRes.data && Object.keys(rankingRes.data).length) setRankingData(prev => ({ ...prev, ...rankingRes.data }));
    } catch (error) {
      console.error('Error fetching analytics data:', error);
    }
  };

  const saveData = async (endpoint, data) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('vyrus_token');
      await axios.post(`${BACKEND_URL}/api/${endpoint}`, { event_id: eventId, ...data }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Data saved successfully');
    } catch (error) {
      toast.error('Failed to save data');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold">Event Analytics Data</h3>
        <p className="text-sm text-muted-foreground">Track detailed metrics for {eventName}</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full grid grid-cols-3 sm:grid-cols-6">
          <TabsTrigger value="customer" className="text-xs"><Users className="h-3 w-3 mr-1" />Customer</TabsTrigger>
          <TabsTrigger value="logistics" className="text-xs"><Truck className="h-3 w-3 mr-1" />Logistics</TabsTrigger>
          <TabsTrigger value="staffing" className="text-xs"><UserCheck className="h-3 w-3 mr-1" />Staff</TabsTrigger>
          <TabsTrigger value="marketing" className="text-xs"><Megaphone className="h-3 w-3 mr-1" />Marketing</TabsTrigger>
          <TabsTrigger value="booth" className="text-xs"><Layout className="h-3 w-3 mr-1" />Booth</TabsTrigger>
          <TabsTrigger value="ranking" className="text-xs"><Award className="h-3 w-3 mr-1" />Ranking</TabsTrigger>
        </TabsList>

        {/* Customer Capture Tab */}
        <TabsContent value="customer" className="mt-4">
          <Card className="bg-card border-border/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Mail className="h-4 w-4 text-primary" />
                Customer Capture
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <InputField label="Emails Collected" name="emails_collected" value={customerData.emails_collected} 
                  onChange={(n, v) => setCustomerData(prev => ({ ...prev, [n]: v }))} />
                <InputField label="SMS Collected" name="sms_collected" value={customerData.sms_collected}
                  onChange={(n, v) => setCustomerData(prev => ({ ...prev, [n]: v }))} />
                <InputField label="IG Follows" name="instagram_follows" value={customerData.instagram_follows}
                  onChange={(n, v) => setCustomerData(prev => ({ ...prev, [n]: v }))} />
                <InputField label="QR Scans" name="qr_scans" value={customerData.qr_scans}
                  onChange={(n, v) => setCustomerData(prev => ({ ...prev, [n]: v }))} />
              </div>
              <div>
                <Label className="text-xs">Requested Items (what customers asked for)</Label>
                <textarea
                  value={customerData.requested_items}
                  onChange={(e) => setCustomerData(prev => ({ ...prev, requested_items: e.target.value }))}
                  placeholder="Characters, designs, products customers asked for..."
                  rows={2}
                  className="w-full px-3 py-2 rounded-lg bg-secondary/50 border-0 text-sm resize-none"
                />
              </div>
              <div>
                <Label className="text-xs">Common Objections</Label>
                <textarea
                  value={customerData.common_objections}
                  onChange={(e) => setCustomerData(prev => ({ ...prev, common_objections: e.target.value }))}
                  placeholder="Price too high, need shipping, wrong size..."
                  rows={2}
                  className="w-full px-3 py-2 rounded-lg bg-secondary/50 border-0 text-sm resize-none"
                />
              </div>
              <Button onClick={() => saveData('customer-capture', customerData)} disabled={saving} className="w-full">
                <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save Customer Data'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Logistics Tab */}
        <TabsContent value="logistics" className="mt-4">
          <Card className="bg-card border-border/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Truck className="h-4 w-4 text-primary" />
                Inventory & Logistics
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <InputField label="Bins/Totes Count" name="bins_totes_count" value={logisticsData.bins_totes_count}
                  onChange={(n, v) => setLogisticsData(prev => ({ ...prev, [n]: v }))} />
                <InputField label="Load-In (min)" name="load_in_minutes" value={logisticsData.load_in_minutes}
                  onChange={(n, v) => setLogisticsData(prev => ({ ...prev, [n]: v }))} />
                <InputField label="Load-Out (min)" name="load_out_minutes" value={logisticsData.load_out_minutes}
                  onChange={(n, v) => setLogisticsData(prev => ({ ...prev, [n]: v }))} />
                <InputField label="Setup (min)" name="setup_minutes" value={logisticsData.setup_minutes}
                  onChange={(n, v) => setLogisticsData(prev => ({ ...prev, [n]: v }))} />
                <InputField label="Teardown (min)" name="teardown_minutes" value={logisticsData.teardown_minutes}
                  onChange={(n, v) => setLogisticsData(prev => ({ ...prev, [n]: v }))} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Missing Items</Label>
                  <Input value={logisticsData.missing_items} onChange={(e) => setLogisticsData(prev => ({ ...prev, missing_items: e.target.value }))}
                    placeholder="List missing items" className="bg-secondary/50 h-9 text-sm" />
                </div>
                <div>
                  <Label className="text-xs">Damaged Items</Label>
                  <Input value={logisticsData.damaged_items} onChange={(e) => setLogisticsData(prev => ({ ...prev, damaged_items: e.target.value }))}
                    placeholder="List damaged items" className="bg-secondary/50 h-9 text-sm" />
                </div>
                <div>
                  <Label className="text-xs">Theft Incidents</Label>
                  <Input value={logisticsData.theft_incidents} onChange={(e) => setLogisticsData(prev => ({ ...prev, theft_incidents: e.target.value }))}
                    placeholder="Any theft?" className="bg-secondary/50 h-9 text-sm" />
                </div>
              </div>
              <Button onClick={() => saveData('event-logistics', logisticsData)} disabled={saving} className="w-full">
                <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save Logistics Data'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Staffing Tab */}
        <TabsContent value="staffing" className="mt-4">
          <Card className="bg-card border-border/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <UserCheck className="h-4 w-4 text-primary" />
                Staffing Metrics
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <InputField label="Staff Count" name="staff_count" value={staffingData.staff_count}
                  onChange={(n, v) => setStaffingData(prev => ({ ...prev, [n]: v }))} />
                <InputField label="Total Staff Hours" name="total_staff_hours" value={staffingData.total_staff_hours}
                  onChange={(n, v) => setStaffingData(prev => ({ ...prev, [n]: v }))} />
              </div>
              <RatingSlider 
                label="Booth Energy Rating" 
                value={staffingData.booth_energy_rating}
                onChange={(v) => setStaffingData(prev => ({ ...prev, booth_energy_rating: v }))}
                description="1 = exhausted/low energy, 10 = high energy engaging customers"
              />
              <div>
                <Label className="text-xs">Notes</Label>
                <textarea value={staffingData.notes} onChange={(e) => setStaffingData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Staffing observations..." rows={2} className="w-full px-3 py-2 rounded-lg bg-secondary/50 border-0 text-sm resize-none" />
              </div>
              <Button onClick={() => saveData('staffing-metrics', staffingData)} disabled={saving} className="w-full">
                <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save Staffing Data'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Marketing Tab */}
        <TabsContent value="marketing" className="mt-4">
          <Card className="bg-card border-border/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Megaphone className="h-4 w-4 text-primary" />
                Marketing Metrics
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-xs text-muted-foreground font-medium">Pre-Show Marketing</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <InputField label="Posts Made" name="pre_show_posts" value={marketingData.pre_show_posts}
                  onChange={(n, v) => setMarketingData(prev => ({ ...prev, [n]: v }))} />
                <InputField label="Reels Views" name="pre_show_reels_views" value={marketingData.pre_show_reels_views}
                  onChange={(n, v) => setMarketingData(prev => ({ ...prev, [n]: v }))} />
                <InputField label="Link Clicks" name="link_clicks" value={marketingData.link_clicks}
                  onChange={(n, v) => setMarketingData(prev => ({ ...prev, [n]: v }))} />
                <InputField label="Preorders" name="preorders_booked" value={marketingData.preorders_booked}
                  onChange={(n, v) => setMarketingData(prev => ({ ...prev, [n]: v }))} />
              </div>
              <p className="text-xs text-muted-foreground font-medium">On-Site Marketing</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <InputField label="QR Scans" name="qr_code_scans" value={marketingData.qr_code_scans}
                  onChange={(n, v) => setMarketingData(prev => ({ ...prev, [n]: v }))} />
                <InputField label="Giveaway Entries" name="giveaway_entries" value={marketingData.giveaway_entries}
                  onChange={(n, v) => setMarketingData(prev => ({ ...prev, [n]: v }))} />
                <InputField label="Discount Uses" name="discount_code_uses" value={marketingData.discount_code_uses}
                  onChange={(n, v) => setMarketingData(prev => ({ ...prev, [n]: v }))} />
                <InputField label="Mention Redemptions" name="mention_redemptions" value={marketingData.mention_redemptions}
                  onChange={(n, v) => setMarketingData(prev => ({ ...prev, [n]: v }))} />
              </div>
              <p className="text-xs text-muted-foreground font-medium">Post-Show Results</p>
              <div className="grid grid-cols-2 gap-3">
                <InputField label="Sales Spike ($)" name="post_show_sales_spike" value={marketingData.post_show_sales_spike}
                  onChange={(n, v) => setMarketingData(prev => ({ ...prev, [n]: v }))} placeholder="24-72hr post-show" />
                <InputField label="New Followers → Buyers" name="new_followers_converted" value={marketingData.new_followers_converted}
                  onChange={(n, v) => setMarketingData(prev => ({ ...prev, [n]: v }))} />
              </div>
              <Button onClick={() => saveData('marketing-metrics', marketingData)} disabled={saving} className="w-full">
                <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save Marketing Data'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Booth Tab */}
        <TabsContent value="booth" className="mt-4">
          <Card className="bg-card border-border/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Layout className="h-4 w-4 text-primary" />
                Booth Performance
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <InputField label="Booth Visitors" name="booth_visitors" value={boothData.booth_visitors}
                  onChange={(n, v) => setBoothData(prev => ({ ...prev, [n]: v }))} />
                <InputField label="Transactions" name="transactions" value={boothData.transactions}
                  onChange={(n, v) => setBoothData(prev => ({ ...prev, [n]: v }))} />
                <InputField label="Stop Rate %" name="stop_rate_estimate" value={boothData.stop_rate_estimate}
                  onChange={(n, v) => setBoothData(prev => ({ ...prev, [n]: v }))} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <InputField label="Card %" name="card_payment_percent" value={boothData.card_payment_percent}
                  onChange={(n, v) => setBoothData(prev => ({ ...prev, [n]: v }))} />
                <InputField label="Cash %" name="cash_payment_percent" value={boothData.cash_payment_percent}
                  onChange={(n, v) => setBoothData(prev => ({ ...prev, [n]: v }))} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Best Wall/Product</Label>
                  <Input value={boothData.best_wall_product} onChange={(e) => setBoothData(prev => ({ ...prev, best_wall_product: e.target.value }))}
                    placeholder="Most attention" className="bg-secondary/50 h-9 text-sm" />
                </div>
                <div>
                  <Label className="text-xs">First Stop Area</Label>
                  <Input value={boothData.first_stop_area} onChange={(e) => setBoothData(prev => ({ ...prev, first_stop_area: e.target.value }))}
                    placeholder="Where people stop first" className="bg-secondary/50 h-9 text-sm" />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <input type="checkbox" checked={boothData.wifi_issues} onChange={(e) => setBoothData(prev => ({ ...prev, wifi_issues: e.target.checked }))}
                  className="h-4 w-4 rounded" />
                <Label className="text-xs">WiFi/Payment Issues</Label>
                <InputField label="Failed Transactions" name="failed_transactions" value={boothData.failed_transactions}
                  onChange={(n, v) => setBoothData(prev => ({ ...prev, [n]: v }))} />
              </div>
              <Button onClick={() => saveData('booth-metrics', boothData)} disabled={saving} className="w-full">
                <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save Booth Data'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Ranking Tab */}
        <TabsContent value="ranking" className="mt-4">
          <Card className="bg-card border-border/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Award className="h-4 w-4 text-primary" />
                Show Ranking
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <RatingSlider label="Profit Score" value={rankingData.profit_score}
                onChange={(v) => setRankingData(prev => ({ ...prev, profit_score: v }))}
                description="1 = lost money, 10 = highly profitable" />
              <RatingSlider label="Stress Score" value={rankingData.stress_score}
                onChange={(v) => setRankingData(prev => ({ ...prev, stress_score: v }))}
                description="1 = relaxed, 10 = extremely stressful" />
              <RatingSlider label="Travel Difficulty" value={rankingData.travel_difficulty_score}
                onChange={(v) => setRankingData(prev => ({ ...prev, travel_difficulty_score: v }))}
                description="1 = easy trip, 10 = nightmare logistics" />
              <RatingSlider label="Brand Growth" value={rankingData.brand_growth_score}
                onChange={(v) => setRankingData(prev => ({ ...prev, brand_growth_score: v }))}
                description="1 = no growth, 10 = great exposure/followers" />
              <div className="flex items-center gap-3 p-3 bg-secondary/30 rounded-lg">
                <input type="checkbox" checked={rankingData.would_do_again}
                  onChange={(e) => setRankingData(prev => ({ ...prev, would_do_again: e.target.checked }))}
                  className="h-5 w-5 rounded" />
                <div>
                  <Label className="text-sm font-medium">Would Do Again?</Label>
                  <p className="text-xs text-muted-foreground">Would you attend this show next year?</p>
                </div>
              </div>
              <div>
                <Label className="text-xs">Notes</Label>
                <textarea value={rankingData.notes} onChange={(e) => setRankingData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Overall thoughts on this show..." rows={2} className="w-full px-3 py-2 rounded-lg bg-secondary/50 border-0 text-sm resize-none" />
              </div>
              <Button onClick={() => saveData('show-ranking', rankingData)} disabled={saving} className="w-full">
                <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save Ranking'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
